import { Box, Container, Heading, SimpleGrid, Text, useColorModeValue, Icon, VStack, Image, HStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { SKILLS } from '@/constants/data';
import { containerVariant, skillItemVariant } from '@/utils/animation';
import SectionDivider from './SectionDivider';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { IconType } from 'react-icons';
import {
  SiEthereum, SiSolidity, SiPython,
  SiTypescript, SiReact, SiNodedotjs, SiPostgresql, SiGit,
  SiJavascript, SiChakraui, SiTailwindcss,
  SiMongodb, SiRedis, SiFirebase,
  SiVisualstudiocode, SiStripe
} from 'react-icons/si';
import { TbBrandNextjs } from 'react-icons/tb';
import { FaBitcoin, FaAws, FaDocker, FaGithub } from 'react-icons/fa';
import { TbBrandGolang } from 'react-icons/tb';

const MotionBox = motion(Box);
const MotionSimpleGrid = motion(SimpleGrid);

interface SkillCardProps {
  title: string;
  items: string[];
  technologies: IconType[];
}

const SkillCard = ({ title, items, technologies }: SkillCardProps) => {
  const bgColor = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.8)');
  const borderColor = useColorModeValue('brand.200', 'brand.700');
  const iconColor = useColorModeValue('brand.500', 'brand.400');
  const hoverBorderColor = useColorModeValue('accent.400', 'accent.300');
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const shadowColor = useColorModeValue('rgba(0, 163, 255, 0.1)', 'rgba(0, 163, 255, 0.2)');

  return (
    <MotionBox
      variants={skillItemVariant}
      bg={bgColor}
      p={8}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow={`0 4px 20px ${shadowColor}`}
      backdropFilter="blur(10px)"
      transition="all 0.3s ease-in-out"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: `0 10px 30px ${shadowColor}`,
        borderColor: hoverBorderColor
      }}
    >
      <VStack spacing={4} align="start">
        <Heading size="md">
          {title}
        </Heading>

        <VStack spacing={4} align="start" w="full">

          <SimpleGrid columns={2} spacing={4} w="full">
            {items.map((item, index) => {
              // Get corresponding technology icon if available
              const TechIcon = technologies[index % technologies.length];
              return (
                <HStack key={index} align="center" spacing={2}>
                  {TechIcon ? (
                    <Icon
                      as={TechIcon}
                      w={4}
                      h={4}
                      color={iconColor}
                      title={TechIcon.name.replace('Si', '')}
                    />
                  ) : (
                    <Box
                      w={2}
                      h={2}
                      borderRadius="full"
                      bg={useColorModeValue('green.500', 'green.200')}
                    />
                  )}
                  <Text
                    fontSize="sm"
                    color={textColor}
                    lineHeight="tall"
                  >
                    {item}
                  </Text>
                </HStack>
              );
            })}
          </SimpleGrid>


        </VStack>
      </VStack>
    </MotionBox>
  );
};

const skillsConfig = [
  { 
    title: 'Blockchain Platforms', 
    items: SKILLS.blockchain, 
    technologies: [SiEthereum, SiSolidity, FaBitcoin, SiEthereum, SiEthereum, SiEthereum] 
  },
  { 
    title: 'Programming Languages', 
    items: SKILLS.languages, 
    technologies: [SiSolidity, SiPython, SiJavascript, SiTypescript, SiPython, SiSolidity] 
  },
  { 
    title: 'Frameworks & Libraries', 
    items: SKILLS.frontend.concat(SKILLS.backend), 
    technologies: [SiReact, TbBrandNextjs, SiNodedotjs, SiChakraui, SiTailwindcss, SiReact, SiNodedotjs, SiChakraui] 
  },
  { 
    title: 'Blockchain Wallets', 
    items: ['MetaMask', 'Trust Wallet', 'WalletConnect', 'Coinbase Wallet', 'Phantom', 'Dapper'], 
    technologies: [SiEthereum, SiEthereum, SiEthereum, SiEthereum, SiEthereum, SiEthereum] 
  },
  { 
    title: 'Payment Gateways', 
    items: ['Stripe', 'Crypto Payments', 'Fiat On/Off Ramp'], 
    technologies: [SiStripe, SiEthereum, SiEthereum] 
  },
  { 
    title: 'Databases & Storage', 
    items: SKILLS.databases.concat(['IPFS', 'Pinata', 'Web3.Storage']), 
    technologies: [SiPostgresql, SiMongodb, SiRedis, SiFirebase, SiPostgresql, SiMongodb] 
  },
  { 
    title: 'Development Tools', 
    items: SKILLS.tools.concat(['Hardhat', 'Truffle', 'Foundry', 'Remix']), 
    technologies: [FaGithub, FaDocker, SiVisualstudiocode, FaGithub, FaDocker, SiVisualstudiocode] 
  },
  { 
    title: 'Smart Contract Features', 
    items: ['ERC Standards', 'DeFi Protocols', 'NFT Marketplaces', 'DAO Governance'], 
    technologies: [SiEthereum, SiEthereum, SiEthereum, SiEthereum] 
  },
];

export default function Skills() {
  const { ref, isVisible } = useIntersectionObserver();
  const bgGradient = useColorModeValue(
    'linear(to-r, brand.50, highlight.50, accent.50)',
    'linear(to-r, brand.900, highlight.900, accent.900)'
  );

  return (
    <Box
      as="section"
      id="skills"
      py={24}
      borderTop="1px solid"
      borderColor={useColorModeValue('gray.200', 'gray.700')}
      bgGradient={useColorModeValue(
        'linear(to-br, purple.50, blue.50)',
        'linear(to-br, purple.900, blue.900)'
      )}
      boxShadow="inset 0 4px 8px rgba(0, 0, 0, 0.05)"
    >
      <Container maxW="7xl">
        <VStack spacing={12}>
          <Box textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="bold"
              mb={4}
            >
              Technical Skills
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
              mx="auto"
            >
              Expertise in blockchain development, full-stack web development, and modern technologies
            </Text>
          </Box>

          <MotionSimpleGrid
            ref={ref}
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={8}
            w="full"
            initial="initial"
            animate={isVisible ? 'animate' : 'initial'}
            variants={containerVariant}
          >
            {skillsConfig.map((skill) => (
              <SkillCard
                key={skill.title}
                title={skill.title}
                items={skill.items}
                icon={skill.icon}
                image={skill.image}
                technologies={skill.technologies || []}
              />
            ))}
          </MotionSimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
}
